// AI Writer v2 (redesign June 2026) — shared constants.

export const STEPS = ['mode', 'setup', 'title', 'draft', 'seo_quality', 'publish'];

export const STEP_LABELS = {
  mode: 'Mode',
  setup: 'Setup',
  title: 'Title',
  draft: 'Draft',
  seo_quality: 'SEO & Quality',
  publish: 'Publish',
};

export const MODES = {
  GUIDED: 'guided_premium',
  FAST: 'fast_draft',
};

export const PAGE_TYPES = [
  { value: 'article', label: 'Article', description: 'Editorial guide with intro, sections, rich content, and FAQ' },
  { value: 'blog_post', label: 'Blog post', description: 'Conversational tone, shorter sections, single CTA' },
  { value: 'product_page', label: 'Product page', description: 'Hero, benefits, features, proof, comparison, CTA' },
  { value: 'service_page', label: 'Service page', description: 'Problem, solution, process, proof, pricing, CTA' },
  { value: 'category_page', label: 'Category page', description: 'Browse-focused with sub-categories and product highlights' },
  { value: 'product_category', label: 'Product category', description: 'SEO-optimized category overview with featured products' },
];

export const SEO_INTENSITIES = [
  { value: 'light', label: 'Light SEO', description: 'Best readability. Uses terms naturally.', tagline: 'best readability', badge: '' },
  { value: 'smart', label: 'Smart SEO', description: 'Best default. Balances readability and POP score.', tagline: 'balanced readability + score', badge: '★ Recommended' },
  { value: 'strong', label: 'Strong SEO', description: 'More complete structure, stronger term coverage, more rich content.', tagline: 'stronger term coverage', badge: '' },
  { value: 'score_first', label: 'Score-first SEO', description: 'Pushes hardest for 100%. Best for competitive pages.', tagline: 'pushes hardest for 100%', badge: '' },
];

export const WRITING_PRIORITIES = [
  { value: 'best_readability', label: 'Best readability - natural prose, fewer forced terms.', badge: '' },
  { value: 'balanced', label: 'Balanced readability + SEO - POP score and reading flow both matter.', badge: 'Default' },
  { value: 'highest_pop_score', label: 'Highest POP score - push for 100% even if rhythm suffers a little.', badge: '' },
];

export const WRITING_STYLES = [
  { value: 'clean_practical', label: 'Clean & practical' },
  { value: 'premium_editorial', label: 'Premium editorial' },
  { value: 'local_expert', label: 'Local expert' },
  { value: 'magazine_style', label: 'Magazine-style' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'technical', label: 'Technical' },
  { value: 'trust_building', label: 'Trust-building' },
];

export const VISUAL_DENSITIES = [
  { value: 'light', label: 'Light' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'magazine_style', label: 'Magazine-style' },
  { value: 'seo_heavy', label: 'SEO-heavy' },
];

// Visual & helpful content components (order matches the prototype table).
// cost = credits per unit (0 = free).
export const VISUAL_COMPONENTS = [
  { key: 'key_highlights', label: 'Key Takeaways', recommended: 'Use 1', cost: 0 },
  { key: 'toc', label: 'Table of Contents', recommended: 'Use 1', cost: 0 },
  { key: 'brief_overview', label: 'Brief Overview', recommended: 'Use 1', cost: 0 },
  { key: 'images', label: 'Images', recommended: 'Use 2-4', cost: 1, warnable: true },
  { key: 'lists', label: 'Lists', recommended: 'Use 1-2', cost: 1, warnable: true },
  { key: 'tables', label: 'Tables', recommended: 'Use 1-2', cost: 1, warnable: true },
  { key: 'forms', label: 'Forms', recommended: 'Use 1-3', cost: 0 },
  { key: 'faq', label: 'Frequently Asked Questions', recommended: 'Use 1', cost: 1 },
  { key: 'internal_links', label: 'Internal links', recommended: 'Use 0-3', cost: 0 },
];

// Upper bound of a component's "Recommended" column (e.g. 'Use 2–4' → 4,
// 'Use 1' → 1). The last number in the string is the recommended max; returns
// null when the string has no number. Used to cap component quantities.
export function recommendedMax(recommended) {
  const nums = (recommended || '').match(/\d+/g);
  return nums && nums.length ? parseInt(nums[nums.length - 1], 10) : null;
}

// Hard cap for "warnable" components (Images/Lists/Tables): double the
// recommended max. Quantities above recommendedMax but at/under this are
// allowed with a warning; above this they are clamped. Returns null when the
// recommended string has no number.
export function recommendedHardMax(recommended) {
  const max = recommendedMax(recommended);
  return max != null ? max * 2 : null;
}

// Density presets bulk-set component quantities.
export const DENSITY_PRESETS = {
  light: { key_highlights: 1, toc: 0, brief_overview: 0, images: 1, lists: 1, tables: 0, forms: 0, faq: 1, internal_links: 0 },
  balanced: { key_highlights: 1, toc: 0, brief_overview: 0, images: 2, lists: 1, tables: 1, forms: 0, faq: 1, internal_links: 0 },
  magazine_style: { key_highlights: 1, toc: 0, brief_overview: 0, images: 3, lists: 2, tables: 1, forms: 0, faq: 1, internal_links: 0 },
  seo_heavy: { key_highlights: 1, toc: 1, brief_overview: 1, images: 3, lists: 3, tables: 2, forms: 1, faq: 2, internal_links: 3 },
};

export const IMAGE_STYLES = [
  { value: 'editorial', label: 'Editorial photography', description: 'Magazine-quality, natural light', icon: '📷' },
  { value: 'cinematic', label: 'Cinematic travel', description: 'Wide, atmospheric scenes', icon: '🎬' },
  { value: 'watercolor', label: 'Watercolor illustration', description: 'Soft, painterly scenes', icon: '🖌' },
  { value: 'flat_vector', label: 'Flat vector art', description: 'Bold, simple shapes', icon: '🔷' },
  { value: 'hand_drawn', label: 'Hand-drawn sketch', description: 'Casual, guidebook feel', icon: '✏' },
  { value: 'vintage_poster', label: 'Vintage poster', description: 'Travel-poster aesthetic', icon: '🎨' },
  { value: 'minimal', label: 'Minimalist conceptual', description: 'Clean, brand-friendly', icon: '◯' },
  { value: 'three_d_render', label: '3D render', description: 'Polished, dimensional', icon: '🧊' },
];

export const MIN_WORD_COUNT = 500;
export const MAX_PRODUCT_DESCRIPTION = 350;
export const MAX_SAMPLE_CONTENT = 500;

// Draft step (Stage 3)
export const VISUAL_PLACEHOLDER_TYPES = [
  { value: 'image', label: 'Image' },
  { value: 'table', label: 'Table' },
  { value: 'list', label: 'List' },
  { value: 'key_highlights', label: 'Key takeaways' },
  { value: 'brief_overview', label: 'Brief overview' },
  { value: 'faq', label: 'FAQ' },
  { value: 'form', label: 'Form' },
  { value: 'sources', label: 'Sources' },
];

export const BLOCK_REGEN_MODES = [
  { value: 'again', label: 'Again' },
  { value: 'shorter', label: 'Shorter' },
  { value: 'longer', label: 'Longer' },
  { value: 'more_premium', label: 'More premium' },
  { value: 'more_practical', label: 'More practical' },
];

export const REGEN_QUICK_SUGGESTIONS = [
  { label: '✦ Fix SEO', text: 'Fix SEO and keyword coverage' },
  { label: '↔ Expand', text: 'Expand this section with more detail' },
  { label: '▦ Improve layout', text: 'Improve the layout - add a list or callout' },
  { label: '✎ Polish', text: 'Polish the flow and phrasing' },
];

export const SUBHEADING_SUGGESTIONS = [
  { label: '⊟ Fewer sections', text: 'Use fewer, broader sections' },
  { label: '⊞ More detailed', text: 'Break the article into more detailed sections' },
  { label: '↕ Reorder', text: "Reorder the sections to follow the reader's journey" },
  { label: '✦ More SEO', text: 'Cover more search intents and keyword themes' },
];

export const BLOCK_TYPE_PILL = {
  intro: 'Intro', h2: 'H2', h3: 'H3', paragraph: 'Text', faq: 'FAQ',
  key_highlights: 'Key takeaways', brief_overview: 'Overview', image: 'Image',
  table: 'Table', list: 'List', form: 'Form', sources: 'Sources',
};

// Default setup used when a fresh session is created.
export function defaultSetup({ targetWordCount = 0 } = {}) {
  const components = {};
  const preset = DENSITY_PRESETS.magazine_style;
  VISUAL_COMPONENTS.forEach((c) => {
    const qty = preset[c.key] || 0;
    components[c.key] = { enabled: qty > 0, quantity: qty };
  });
  return {
    content_source: { type: 'write_new', pasted_html: '', import_url: '', imported_text: '', imported_word_count: 0 },
    word_count_mode: 'recommended',
    word_count: targetWordCount || MIN_WORD_COUNT,
    language: 'en',
    page_type: 'article',
    competitors: [],
    seo_intensity: 'smart',
    writing_priority: 'balanced',
    content_style: ['premium_editorial', 'local_expert'],
    visual_density: 'magazine_style',
    visual_components: components,
    image_style: 'editorial',
    internal_links: { enabled: false, top_level_url: '', supporting_urls: ['', ''] },
    advanced: {
      author_perspective: '',
      brand_name: '',
      target_audience: '',
      product_service_description: '',
      sample_content: '',
      excluded_words: { hard: [], soft: [] },
      research_level: 'light',
      // Opt-in "authoritative web sources" research (default off; +1 credit).
      use_web_sources: false,
      structure_source: 'use_competitors',
      api_mode: 'pop',
    },
  };
}
